// @flow
import { useLoadCustomer } from 'queries/customers/useLoadCustomer'
import { useAddRoutines } from 'queries/routines/useAddRoutines'
import { useUpdateRoutines } from 'queries/routines/useUpdateRoutines'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { RoutineType } from 'types/routines'
import Ui from 'ui'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Heading from 'ui/atoms/Heading'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import BlockList from 'ui/molecules/BlockList'
import DateField from 'ui/molecules/DateField'
import TextField from 'ui/molecules/TextField'
import { ZentakeContext } from 'utils/ZentakeContext'

import { QUERIES } from '@/enums/queries'
import { loadFormOptions } from '@/queries/forms/services/loadFormOptions'
import { loadPacketOptions } from '@/queries/packets/services/loadPacketOptions'
import { FormAsyncSelect } from '@/ui/form/antd/FormAsyncSelect'
import Modal from '@/ui/Modal'

type Props = {
  onClose: () => void,
  routine: RoutineType | void,
}

function CreateEditRoutineModal({ onClose, routine }: Props): React.Node {
  const { clientId } = useParams()
  const { data: clientInDetail } = useLoadCustomer(clientId)
  const { addRoutine, isLoading: isAddLoading } = useAddRoutines()
  const { updateRoutine, isLoading: isUpdateLoading } = useUpdateRoutines()
  const routinesLoading = isAddLoading || isUpdateLoading

  const { updatePackets, updateForms } = React.useContext(ZentakeContext)

  // eslint-disable-next-line no-unused-vars
  const [willUseEmail, setWillUseEmail] = React.useState(true)
  const [willSendForm, setWillSendForm] = React.useState(true)
  // eslint-disable-next-line no-unused-vars
  const [changesOn, setChangesOn] = React.useState<'never' | 'on' | 'after'>(
    'after'
  )
  const [data, setData] = React.useState<RoutineType>(
    routine ||
      ({
        uid: '',
        name: '',
        form: null,
        packet: null,
        document: null,
        customer: clientInDetail?.id || '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: null,
        repeat_type: 'Daily',
        repeat_interval: 1,
        customer_email: clientInDetail?.email || null,
        customer_phone_number: null,
        end_after_occurrences: 2,
      }: RoutineType)
  )

  const [errors, setErrors] = React.useState({})

  const repeatTypeOptions = React.useMemo(() => {
    if (data.repeat_interval && data.repeat_interval > 1) {
      return [
        { label: 'Days', value: 'Daily' },
        { label: 'Weeks', value: 'Weekly' },
        { label: 'Months', value: 'Monthly' },
        { label: 'Years', value: 'Yearly' },
      ]
    }
    return [
      { label: 'Day', value: 'Daily' },
      { label: 'Week', value: 'Weekly' },
      { label: 'Month', value: 'Monthly' },
      { label: 'Year', value: 'Yearly' },
    ]
  }, [data.repeat_interval])

  React.useEffect(() => {
    updatePackets()
    updateForms()
    // eslint-disable-next-line
  }, [])

  const validate = React.useCallback(() => {
    const _errors = ({}: any)
    if (!data.form && willSendForm) {
      _errors.form = 'Form is required'
    }
    if (!data.packet && !willSendForm) {
      _errors.packet = 'Packet is required'
    }
    if (!data.customer_email && willUseEmail) {
      _errors.email = 'Email is required'
    }
    if (!data.customer_phone_number && !willUseEmail) {
      _errors.phone = 'Phone number is required'
    }
    if (!data.repeat_interval) {
      _errors.repeat_interval = 'Repeat interval is required'
    }
    if (!data.repeat_type) {
      _errors.repeat_type = 'Repeat type is required'
    }

    if (changesOn === 'after' && !data.end_after_occurrences) {
      _errors.end_after_occurrences = 'End occurrences is required'
    }
    setErrors(_errors)
    return !Object.keys(_errors).length
  }, [
    changesOn,
    data.customer_email,
    data.customer_phone_number,
    data.end_after_occurrences,
    // data.end_date,
    data.form,
    data.packet,
    data.repeat_interval,
    data.repeat_type,
    // data.start_date,
    willSendForm,
    willUseEmail,
  ])

  const handleSubmit = React.useCallback(async () => {
    if (!validate()) {
      return
    }
    try {
      if (routine) {
        await updateRoutine({
          customerId: clientInDetail?.id || '',
          id: routine?.uid,
          data,
        })
      } else {
        await addRoutine({ customerId: clientInDetail?.id || '', data })
      }
      toast.success('Scheduled Form saved successfully')
      onClose()
    } catch (e) {
      toast.error('Failed to save Scheduled Form')
    }
  }, [
    addRoutine,
    clientInDetail?.id,
    data,
    onClose,
    routine,
    updateRoutine,
    validate,
  ])

  const handleChange = React.useCallback(
    (key, value) => {
      // $FlowFixMe
      setData((prev) => ({ ...prev, [key]: value }))
    },
    [setData]
  )

  const handleEndsChange = React.useCallback(
    (value) => {
      if (changesOn === 'on') {
        handleChange('end_date', value)
        handleChange('end_after_occurrences', null)
      } else if (changesOn === 'after') {
        handleChange('end_after_occurrences', value)
        handleChange('end_date', null)
      } else {
        handleChange('end_date', null)
        handleChange('end_after_occurrences', null)
      }
    },
    [changesOn, handleChange]
  )

  return (
    <Modal
      open
      footer={() => undefined}
      maskClosable={false}
      onCancel={onClose}
    >
      <Space size="large">
        <div>
          <Heading>{routine ? 'Edit' : 'Create'} Scheduled Form</Heading>
          <Text $light $small className="mt-2">
            Automate sending forms. Select the form you need, specify how often
            it should be sent to your client.
          </Text>
        </div>
        <form>
          <Space size="regular">
            <BlockList
              items={[
                {
                  active: willSendForm,
                  onClick: () => setWillSendForm(true),
                  label: 'Send a Form',
                },

                {
                  active: !willSendForm,
                  onClick: () => setWillSendForm(false),
                  label: 'Send a Packet',
                },
              ]}
              size="medium"
            />
            {willSendForm === true ? (
              <FormAsyncSelect
                infiniteScroll
                isRequired
                loadOnMount
                showSearch
                errorLabel={errors.form}
                fetchOptions={loadFormOptions}
                hasError={!!errors.form}
                label="Form to Send"
                onChange={(item) => handleChange('form', String(item))}
                placeholder="Select Form"
                queryKey={[QUERIES.FORMS.LOAD_TO_SELECT]}
                searchKey="search"
                value={data.form}
              />
            ) : (
              <FormAsyncSelect
                infiniteScroll
                isRequired
                loadOnMount
                showSearch
                errorLabel={errors.packet}
                fetchOptions={loadPacketOptions}
                hasError={!!errors.packet}
                label="Packet to Send"
                onChange={(item) => handleChange('packet', String(item))}
                othersParams={{
                  simplified: 'true',
                  without_disabled: 'false',
                  page_size: '50',
                }}
                placeholder="Select Form"
                queryKey={[QUERIES.PACKETS.LOAD_TO_SELECT]}
                searchKey="search"
                value={data.packet}
              />
            )}
            <FlexBox>
              <Flex>
                <FlexBox>
                  <Ui.QuestionTitle>
                    {willUseEmail ? 'Email' : 'Phone'}
                  </Ui.QuestionTitle>
                </FlexBox>
                {/* <Icons.ChatOutline
                  color={willUseEmail ? 'text-txt' : 'text-zentake'}
                />
                <Ui.Toggle
                  primary
                  primaryIdle
                  onChange={() => setWillUseEmail((previous) => !previous)}
                  value={willUseEmail}
                />
                <Icons.MailOutline
                  color={willUseEmail ? 'text-zentake' : 'text-txt'}
                /> */}
              </Flex>
              {willUseEmail ? (
                <TextField
                  name="person_email"
                  onChange={(v) => handleChange('customer_email', v)}
                  placeholder="Email"
                  type="email"
                  value={data.customer_email}
                />
              ) : (
                <Ui.SingleLinePhone
                  name="person_phone"
                  onChange={(v) => handleChange('customer_phone_number', v)}
                  placeholder="Phone Number"
                  type="phone"
                  value={data.customer_phone_number}
                />
              )}
            </FlexBox>
            <FlexBox>
              <DateField
                semibold
                label="Start Date"
                name="start_date"
                onChange={(v) => handleChange('start_date', v ? v[1] : null)}
                value={data.start_date}
              />
            </FlexBox>
            <FlexBox className="mt-4">
              <Flex $horizontalSpacing="small" align="center">
                <Ui.QuestionTitle>Send every</Ui.QuestionTitle>
                <Ui.NumericText
                  $medium
                  errorLabel={errors?.repeat_interval}
                  hasError={errors?.repeat_interval}
                  name="repeat_interval"
                  onChange={(v) => handleChange('repeat_interval', v)}
                  value={data.repeat_interval}
                />
                <Ui.Dropdown
                  $medium
                  hasError={errors?.repeat_type}
                  name="repeat_type"
                  onChange={(v) => handleChange('repeat_type', v)}
                  options={repeatTypeOptions}
                  value={data.repeat_type}
                />
              </Flex>

              <Flex $flexDirection="col" className="mt-3">
                <Ui.QuestionTitle>
                  How many times should this form be sent?
                </Ui.QuestionTitle>
                <Ui.NumericText
                  $large
                  disabled={changesOn !== 'after'}
                  errorLabel={errors?.end_after_occurrences}
                  hasError={errors?.end_after_occurrences}
                  name="end_occurrences"
                  onChange={(v) => handleEndsChange(v)}
                  value={data.end_after_occurrences}
                />
              </Flex>
            </FlexBox>
          </Space>
        </form>
        <Flex $horizontalSpacing="medium" justify="end">
          <Button onClick={onClose}>Cancel</Button>
          <Button $primary loading={routinesLoading} onClick={handleSubmit}>
            {routine ? 'Update' : 'Start'}
          </Button>
        </Flex>
      </Space>
    </Modal>
  )
}

export default CreateEditRoutineModal
